import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Response } from "../types";

import { ENDPOINTS } from "@/api/application-api";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayToast } from "@/utils/toast";

interface RerunReportColumnResponse {
  success: boolean;
  message: string;
  report_column_id: number;
}

export const useRerunReportColumn = (userToken: string, reportId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    Response<RerunReportColumnResponse, "report_column", string>,
    Error,
    { reportColumnId: number }
  >({
    mutationFn: async ({ reportColumnId }) => {
      const url = new URL(ENDPOINTS.report_column_rerun(reportColumnId));

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);

      try {
        const response = await fetch(url.toString(), {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return await handleResponse(response);
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Request timed out after 35 seconds");
        }
        throw error;
      }
    },
    onSuccess: response => {
      if (response.success) {
        // Invalidate relevant queries to refresh data
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);

        displayToast(response.message || "Column rerun queued successfully", ToastType.success);
      }
    },
    onError: error => {
      displayToast(error.message || "Failed to rerun column", ToastType.error);
    },
  });
};
